/**
 * Writing and signing a note, and correcting one (PHASE-0.md §2.5).
 *
 * **One screen for both.** A correction is the same form with the current text in it, and
 * that is not a shortcut — on an append-only record a correction *is* a note, written in
 * full and signed in full. A separate "edit" form would be a different shape for the same
 * act and would invite the idea that one of them modifies something.
 *
 * **Nothing is saved until Sign.** DECISIONS.md #38: there is no draft table, no
 * `localStorage`, no autosave. The editor says so on screen in
 * `notes:editor.nothingSaved`, warns before discarding text, and warns before the tab
 * closes — because the mitigation for "the draft is only in memory" is an interface that
 * never loses it quietly, and that is the whole mitigation there is.
 *
 * **Signing is one click and it is final.** There is no confirmation step before it: a
 * clinician who has typed a note and pressed Sign meant to sign it, and an extra dialogue
 * is the thing that gets clicked through. What the screen does instead is say plainly,
 * before the button, that a signed note cannot be removed and that mistakes are corrected
 * by writing a new version (AGENTS.md §3 rule 2). That is true, it is short, and it is the
 * thing a clinician needs to know once.
 *
 * **A correction reads the note first**, rather than being handed text by the screen that
 * opened it. The overview's note list carries `version` but not `version_id`, and the
 * amendment endpoint needs the row identity — so the prefilled text and the `supersedes`
 * come out of the same response and cannot disagree about which version is being
 * corrected. It is also what makes recovering from `NOTE_VERSION_STALE` the same code path
 * as opening the editor.
 */

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { languageName, languages } from '../i18n/catalogues'

import {
  amendNote,
  currentVersion,
  fetchNoteVersions,
  NoteHistoryError,
  NoteRefusedError,
  NoteWriteUnavailableError,
  signNote,
  type NoteHistoryFailure,
  type NoteRefusalCode,
  type NoteRefusalParams,
  type NoteWritten,
} from './noteApi'
import {
  draftProblemMessage,
  noteFieldHint,
  noteFieldLabel,
  noteRefusalMessage,
  noteWriteUnavailableMessage,
  offeredNoteTypeLabel,
  type NoteTranslate,
} from './noteDisplay'
import {
  draftFrom,
  draftProblem,
  emptyDraft,
  initialNoteLanguage,
  isDraftDirty,
  MAXIMUM_FIELD_LENGTH,
  NOTE_FIELDS,
  OFFERED_NOTE_TYPES,
  toSubmission,
  UNCHOSEN_LANGUAGE,
  type NoteDraft,
  type NoteField,
} from './noteDraft'

/** How many rows each box gets. `bodyText` is last and largest — it is the overflow. */
const FIELD_ROWS: Readonly<Record<NoteField, number>> = {
  subjective: 3,
  objective: 3,
  assessment: 3,
  plan: 3,
  bodyText: 6,
}

type Phase =
  /** Reading the note being corrected. A new note skips this. */
  | { readonly kind: 'opening' }
  /** The note could not be read, so there is nothing to correct against. */
  | { readonly kind: 'unopenable'; readonly failure: NoteHistoryFailure }
  | { readonly kind: 'editing' }
  /** A write is in flight. The form is read-only and Sign cannot be pressed twice. */
  | { readonly kind: 'signing' }
  /** Written. The form is gone, so the same text cannot be signed a second time. */
  | { readonly kind: 'written'; readonly written: NoteWritten }

type Notice =
  | {
      readonly kind: 'refused'
      readonly code: NoteRefusalCode | 'UNKNOWN'
      readonly params: NoteRefusalParams
    }
  | { readonly kind: 'unavailable' }
  /** Something this side found before sending — same wording as the server's. */
  | { readonly kind: 'problem'; readonly message: string }

function Field({
  field,
  value,
  language,
  disabled,
  autoFocus,
  onChange,
  t,
}: {
  readonly field: NoteField
  readonly value: string

  /** The note's language, for `lang` — spell-check and the reading view's markup. Empty when unchosen. */
  readonly language: string
  readonly disabled: boolean
  readonly autoFocus: boolean
  readonly onChange: (value: string) => void
  readonly t: NoteTranslate
}) {
  const id = `note-field-${field}`
  const hintId = `${id}-hint`

  return (
    <div className="note-editor__field">
      <label htmlFor={id}>{noteFieldLabel(field, t)}</label>

      <p id={hintId} className="note-editor__hint">
        {noteFieldHint(field, t)}
      </p>

      <textarea
        id={id}
        value={value}
        rows={FIELD_ROWS[field]}
        disabled={disabled}
        autoFocus={autoFocus}
        aria-describedby={hintId}
        // The note's language, on the element the text is typed into: it is what makes the
        // browser's spell-check Swedish rather than the interface's language, and it
        // matches the `lang` the reading view puts on the same text.
        lang={language === '' ? undefined : language}
        // `maxLength` as well as the check in `draftProblem`, because stopping at the limit
        // is kinder than refusing a note after 10,000 characters were typed. The server
        // still enforces it — this is not the guard, it is the courtesy.
        maxLength={MAXIMUM_FIELD_LENGTH}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  )
}

/**
 * The note form.
 *
 * `amendingNoteId` decides which of the two things this is; everything else is the same.
 * `onWritten` fires as soon as the write succeeds so the screen underneath can refetch,
 * and the editor stays open on a confirmation until the clinician closes it — seeing that
 * it was signed is part of signing it.
 */
export function NoteEditor({
  patientId,
  amendingNoteId,
  onWritten,
  onClose,
}: {
  readonly patientId: string

  /** The note being corrected, or null for a new one. */
  readonly amendingNoteId: string | null

  readonly onWritten: (written: NoteWritten) => void
  readonly onClose: () => void
}) {
  const { t, i18n } = useTranslation(['common', 'patient', 'notes'])

  const amending = amendingNoteId !== null

  const [phase, setPhase] = useState<Phase>(() =>
    amending ? { kind: 'opening' } : { kind: 'editing' },
  )

  /**
   * The text. Set once — when the form opens — and never again from outside.
   *
   * That is what the stale-version recovery below depends on: reloading the note must not
   * overwrite what the clinician has typed. A genuinely new editing session is a remount;
   * the parent gives this component a `key`.
   */
  const [draft, setDraft] = useState<NoteDraft>(() =>
    emptyDraft(
      undefined,
      initialNoteLanguage((i18n.resolvedLanguage ?? i18n.language).replace(/-.*$/, '')),
    ),
  )

  /** What the draft started as, for the unsaved-text question. */
  const opened = useRef<NoteDraft>(draft)

  /**
   * The version being corrected.
   *
   * Read from the record rather than from a prop, and it moves when the clinician reloads
   * after `NOTE_VERSION_STALE` — that is the one thing recovery has to change, and the text
   * is the one thing it must not.
   */
  const [supersedes, setSupersedes] = useState<string | null>(null)

  /** The version number the correction will become, for the explanatory line. */
  const [nextVersion, setNextVersion] = useState<number | null>(null)

  const [notice, setNotice] = useState<Notice | null>(null)
  const [confirmingDiscard, setConfirmingDiscard] = useState(false)
  const [reloading, setReloading] = useState(false)

  /**
   * Whether a `setState` would still be seen.
   *
   * A submit in flight is deliberately **not** aborted on unmount — see `noteApi.post`. A
   * write that reached the server happened, and cancelling the request would not undo it,
   * it would only hide the answer. So the request runs to completion and this stops it
   * writing into a component that is gone.
   */
  const live = useRef(true)
  useEffect(() => {
    live.current = true
    return () => {
      live.current = false
    }
  }, [])

  const dirty = isDraftDirty(draft, opened.current)
  const busy = phase.kind === 'signing'

  /**
   * Reads the note being corrected: its current text, and the version to supersede.
   *
   * `prefill` is false when this is the stale-version recovery. The clinician's text stays
   * on screen — they were told to read the new version and decide whether their correction
   * still says what they mean, and silently replacing their words with somebody else's
   * would be the opposite of that.
   */
  const open = (prefill: boolean) => {
    if (amendingNoteId === null) {
      return
    }

    const controller = new AbortController()

    fetchNoteVersions(patientId, amendingNoteId, controller.signal).then(
      (history) => {
        if (!live.current) {
          return
        }

        const latest = currentVersion(history)
        if (latest === null) {
          // `NoteVersions` promises the list is never empty — a note with no versions is a
          // 404. Treated as not found rather than trusted, because the alternative is an
          // editor with no `supersedes` that would post as a first version.
          setPhase({ kind: 'unopenable', failure: 'not-found' })
          return
        }

        setSupersedes(latest.versionId)
        setNextVersion(latest.version + 1)

        if (prefill) {
          const prefilled = draftFrom(latest)
          setDraft(prefilled)
          opened.current = prefilled
          setPhase({ kind: 'editing' })
        }

        setNotice(null)
        setReloading(false)
      },
      (cause: unknown) => {
        if (!live.current) {
          return
        }
        setReloading(false)

        const failure: NoteHistoryFailure =
          cause instanceof NoteHistoryError ? cause.failure : 'unavailable'

        if (prefill) {
          // Nothing has been typed yet, so there is nothing to protect: say the note could
          // not be opened rather than presenting an empty form that would post a *new*
          // note if it were signed.
          setPhase({ kind: 'unopenable', failure })
        } else {
          setNotice({ kind: 'unavailable' })
        }
      },
    )
  }

  // Opens once, on mount, and only for a correction.
  //
  // **The empty dependency array is the point, not an omission.** `open` closes over the
  // props, and this must not re-run when they change: a second run would refetch and
  // replace text a clinician has typed, which #38 means exists nowhere else. The props do
  // not change without a remount — the parent gives this component a `key` — so there is
  // nothing to react to.
  useEffect(() => {
    open(true)
  }, [])

  /**
   * The browser's own "leave this page?" question, while there is text.
   *
   * The only guard against a reload or a closed tab, and it is here because #38 means there
   * is nothing to come back to. The wording is the browser's and cannot be set, which is
   * why the on-screen sentence says it too.
   */
  useEffect(() => {
    if (!dirty || phase.kind === 'written') {
      return
    }

    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()

      // `preventDefault()` alone is the current spec, and Chrome has honoured it only since
      // 119. Firefox and Safari cancel on a **non-empty** `returnValue` instead, so without
      // this line the reload goes through silently and the text is gone — which is what
      // happened the first time this screen met a browser. Both, therefore.
      //
      // The value is never displayed. Every engine since about 2017 prints its own wording
      // and ignores this string, which is why it is not a catalogue key (AGENTS.md §7) — it
      // is a cancel flag that happens to be typed as text.
      event.returnValue = 'unsaved'
    }

    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty, phase.kind])

  const update = (field: NoteField | 'noteType' | 'language', value: string) => {
    setDraft((previous) => ({ ...previous, [field]: value }))

    // A refusal describes text that has since changed. Keeping it on screen while the
    // clinician fixes it would leave them reading an error about something they have
    // already corrected.
    setNotice(null)
  }

  const submit = () => {
    if (busy) {
      return
    }

    const problem = draftProblem(draft)
    if (problem !== null) {
      // Checked here rather than by disabling the button. A greyed-out Sign with no
      // explanation is the worse failure in a stressed setting: the clinician cannot tell
      // whether the system is broken or they have missed something, and the message is the
      // same one the server would have sent.
      setNotice({ kind: 'problem', message: draftProblemMessage(problem, t) })
      return
    }

    if (amendingNoteId !== null && supersedes === null) {
      // Refusing to post rather than falling back to `signNote`, which would write a
      // second, unrelated note that no `DELETE` can remove (AGENTS.md §3).
      setNotice({ kind: 'unavailable' })
      return
    }

    setPhase({ kind: 'signing' })
    setNotice(null)

    const controller = new AbortController()
    const submission = toSubmission(draft)

    const written =
      amendingNoteId === null || supersedes === null
        ? signNote(patientId, submission, controller.signal)
        : amendNote(patientId, amendingNoteId, supersedes, submission, controller.signal)

    written.then(
      (result) => {
        if (!live.current) {
          return
        }
        setPhase({ kind: 'written', written: result })

        // The screen underneath refetches now, so the note is visible behind the
        // confirmation rather than after it is dismissed.
        onWritten(result)
      },
      (cause: unknown) => {
        if (!live.current) {
          return
        }

        // Back to editing with the text untouched, whatever went wrong. The draft is the
        // only copy.
        setPhase({ kind: 'editing' })

        if (cause instanceof NoteRefusedError) {
          setNotice({ kind: 'refused', code: cause.code, params: cause.params })
        } else if (cause instanceof NoteWriteUnavailableError) {
          setNotice({ kind: 'unavailable' })
        } else {
          setNotice({ kind: 'refused', code: 'UNKNOWN', params: {} })
        }
      },
    )
  }

  /** Recovery from `NOTE_VERSION_STALE`: point at the version that is current now. */
  const reload = () => {
    if (reloading) {
      return
    }
    setReloading(true)
    open(false)
  }

  const close = () => {
    if (dirty && phase.kind === 'editing') {
      setConfirmingDiscard(true)
      return
    }
    onClose()
  }

  const heading = amending ? t('notes:editor.headingAmend') : t('notes:editor.headingNew')

  if (phase.kind === 'opening') {
    return (
      <section className="note-editor" aria-labelledby="note-editor-heading">
        <h2 id="note-editor-heading">{heading}</h2>
        <p className="note-editor__notice" aria-live="polite">
          {t('notes:history.loading')}
        </p>
      </section>
    )
  }

  if (phase.kind === 'unopenable') {
    return (
      <section className="note-editor" aria-labelledby="note-editor-heading">
        <h2 id="note-editor-heading">{heading}</h2>

        <div className="note-editor__notice" role="alert">
          <p>
            {phase.failure === 'not-found'
              ? t('notes:history.notFound')
              : t('notes:history.failed')}
          </p>
        </div>

        <button type="button" onClick={onClose}>
          {t('notes:editor.close')}
        </button>
      </section>
    )
  }

  if (phase.kind === 'written') {
    return (
      <section
        className="note-editor note-editor--written"
        aria-labelledby="note-editor-heading"
      >
        <h2 id="note-editor-heading">{heading}</h2>

        {/* `status`, not `alert`: it is good news, and it must not interrupt. */}
        <p className="note-editor__written" role="status">
          {amending
            ? t('notes:editor.amendedOk', { version: phase.written.version })
            : t('notes:editor.signedOk')}
        </p>

        <button type="button" onClick={onClose}>
          {t('notes:editor.close')}
        </button>
      </section>
    )
  }

  return (
    <section className="note-editor" aria-labelledby="note-editor-heading">
      <header className="note-editor__header">
        <h2 id="note-editor-heading">{heading}</h2>

        {/* Said before the form, not beside the button: a clinician has to know that
            nothing is being saved while they type, not after they have typed. */}
        <p className="note-editor__nothing-saved">{t('notes:editor.nothingSaved')}</p>

        {nextVersion !== null && (
          <p className="note-editor__amend-explains">
            {t('notes:editor.amendExplanation', { version: nextVersion })}
          </p>
        )}
      </header>

      <div className="note-editor__type">
        <label htmlFor="note-editor-type">{t('notes:editor.noteType')}</label>
        <select
          id="note-editor-type"
          value={draft.noteType}
          disabled={busy}
          onChange={(event) => update('noteType', event.target.value)}
        >
          {/* The offered vocabulary, not the permitted one — `note_type` has no CHECK
              against a list (migration 0007). A note already carrying a type this build
              does not offer keeps it: it is in the list because it is the draft's value. */}
          {[...new Set<string>([...OFFERED_NOTE_TYPES, draft.noteType])].map((noteType) => (
            <option key={noteType} value={noteType}>
              {offeredNoteTypeLabel(noteType, t)}
            </option>
          ))}
        </select>
      </div>

      {/* What the text is written in (DECISIONS.md #54) — beside the text, never from the menu. */}
      <div className="note-editor__type">
        <label htmlFor="note-editor-language">{t('notes:editor.language')}</label>
        <select
          id="note-editor-language"
          value={draft.language}
          disabled={busy}
          onChange={(event) => update('language', event.target.value)}
        >
          {draft.language === UNCHOSEN_LANGUAGE && (
            <option value={UNCHOSEN_LANGUAGE}>{t('notes:editor.languageChoose')}</option>
          )}
          {/* Every bundled language, by its own name, and the draft's own if it is another — a
              correction of a note in a language this build has no catalogue for keeps it.

              **Not narrowed by CONTINUUM_LANGUAGES** (DECISIONS.md #57, correcting #56). That setting
              is about which *menus* a site shows its staff; this is what the *text* is written in. A
              site that keeps the unreviewed Spanish menus off must still let a visiting team label
              their Spanish note as Spanish — the mislabel #54 exists to prevent. */}
          {[...new Set<string>([...languages, ...(draft.language === UNCHOSEN_LANGUAGE ? [] : [draft.language])])].map(
            (language) => (
              <option key={language} value={language}>
                {languages.includes(language) ? languageName(language) : language}
              </option>
            ),
          )}
        </select>
      </div>

      {NOTE_FIELDS.map((field, index) => (
        <Field
          key={field}
          field={field}
          language={draft.language}
          value={draft[field]}
          disabled={busy}
          // The cursor starts in the first box. On a correction that is also where the text
          // already is, which is the right place to begin reading it.
          autoFocus={index === 0}
          onChange={(value) => update(field, value)}
          t={t}
        />
      ))}

      {/* Announced, because a refusal is the whole message on this screen and the
          clinician's attention is on the text they just tried to save. */}
      <div aria-live="assertive">
        {notice !== null && (
          <div className="note-editor__notice" role="alert">
            <p>
              {notice.kind === 'problem'
                ? notice.message
                : notice.kind === 'unavailable'
                  ? noteWriteUnavailableMessage(t)
                  : noteRefusalMessage(notice.code, notice.params, t)}
            </p>

            {/* Only the stale correction has anything to do about it. Everything else is
                either fixed in the form or retried with the same button. */}
            {notice.kind === 'refused' && notice.code === 'NOTE_VERSION_STALE' && (
              <button type="button" disabled={reloading} onClick={reload}>
                {t('notes:editor.reload')}
              </button>
            )}
          </div>
        )}
      </div>

      <footer className="note-editor__actions">
        {/* The one thing about signing that a clinician needs to know once, said where the
            decision is made rather than in a dialogue that gets clicked through. */}
        <p className="note-editor__final">{t('notes:editor.signedIrreversible')}</p>

        <button
          type="button"
          className="note-editor__sign"
          // Disabled only while a write is in flight — a second press would write a second
          // note, and there is no `DELETE` (AGENTS.md §3). Never disabled for a problem in
          // the form; see `submit`.
          disabled={busy}
          onClick={submit}
        >
          {busy ? t('notes:editor.signing') : t('notes:editor.sign')}
        </button>

        <button type="button" disabled={busy} onClick={close}>
          {t('notes:editor.cancel')}
        </button>
      </footer>

      {confirmingDiscard && (
        <div
          className="note-editor__discard"
          role="alertdialog"
          aria-label={t('notes:editor.discardQuestion')}
        >
          <p>{t('notes:editor.discardQuestion')}</p>

          {/* Keep writing is first and is the safe one. The destructive button says what it
              does rather than "OK" — this is the click that loses the text. */}
          <button type="button" onClick={() => setConfirmingDiscard(false)}>
            {t('notes:editor.discardCancel')}
          </button>
          <button type="button" onClick={onClose}>
            {t('notes:editor.discardConfirm')}
          </button>
        </div>
      )}
    </section>
  )
}
